import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import HomeIcon from "@mui/icons-material/Home";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import ViewListIcon from "@mui/icons-material/ViewList";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import AssessmentIcon from "@mui/icons-material/Assessment";
import MoneyIcon from "@mui/icons-material/Money";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import SosIcon from "@mui/icons-material/Sos";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import ChatIcon from "@mui/icons-material/Chat";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import PublicIcon from "@mui/icons-material/Public";
import ArticleIcon from "@mui/icons-material/Article";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { api } from "common";
import { calcEst, showEst, optionsRequired, labelText, FONT_FAMILY } from "../common/sharedFunctions";
import garahexLogo from "../assets/garahex-logo-white.png";
import adminTokens from "../styles/adminDesignTokens";

const expandedWidth = adminTokens.layout.sidebarExpanded;
const collapsedWidth = adminTokens.layout.sidebarCollapsed;

const oldAccessFallback = (role, item) => {
  if (!item) return false;
  if (item.alwaysVisible) return true;
  if (!role) return false;
  if (!Array.isArray(item.access) || item.access.length === 0) return true;
  return item.access.includes(role);
};

const canViewSidebarItem = ({ role, item, accessPermissions }) => {
  try {
    if (!item) return false;
    if (item.alwaysVisible || item.moduleKey === "profile") return true;

    const oldAccessAllowed = oldAccessFallback(role, item);

    if (role === "admin") return oldAccessAllowed;

    if (!accessPermissions || !accessPermissions.permissions || !item.moduleKey) {
      return oldAccessAllowed;
    }

    const viewPermission = accessPermissions.permissions?.[role]?.[item.moduleKey]?.view;
    if (viewPermission === true) return true;
    if (viewPermission === false) return false;

    return oldAccessAllowed;
  } catch (error) {
    console.warn("Sidebar permission fallback:", error);
    return oldAccessFallback(role, item);
  }
};

const humanizeRole = (role) => {
  if (!role) return "";
  return String(role).replace(/([a-z])([A-Z])/g, "$1 $2").replace(/fleetadmin/i, "Fleet Admin");
};

const getProfileName = (profile = {}) => {
  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim();
  return profile.companyName || profile.businessName || profile.name || fullName || profile.email || "Garahex Admin";
};

const getInitials = (name) => {
  const parts = String(name || "GA").trim().split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] || "G") + (parts[1]?.[0] || "A");
};

const getPageTitle = (pathname, groups) => {
  const items = groups.flatMap((group) => group.items || []);
  const exact = items.find((item) => item.url && item.url === pathname);
  if (exact) return exact.name;
  const nested = items
    .filter((item) => item.url && item.url !== "/" && pathname.startsWith(item.url))
    .sort((a, b) => b.url.length - a.url.length)[0];
  return nested?.name || "Admin Workspace";
};

const isActivePath = (pathname, item) => {
  if (!item.url || item.url === "logout") return false;
  if (item.url === "/") return pathname === "/";
  if (item.url === "/website-management") return pathname === "/website-management";
  return pathname === item.url || pathname.startsWith(`${item.url}/`);
};

export default function AdminShell({ children, window }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const isRTL = i18n.dir() === "rtl";
  const isTabletDown = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [role, setRole] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);

  const label = useCallback((key, fallback) => labelText(t, key, fallback), [t]);

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) {
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  useEffect(() => {
    if (isTabletDown) {
      setCollapsed(false);
    }
  }, [isTabletDown]);

  const handleSignOff = () => {
    dispatch(api.signOff());
  };

  const accessPermissions = settings && settings.accessPermissions ? settings.accessPermissions : null;
  const profileName = getProfileName(auth.profile);
  const appName = settings && settings.appName ? settings.appName : "Garahex";

  const navGroups = useMemo(() => {
    const websiteAdminAccess = ["admin"];

    return [
      {
        label: "Dashboard",
        items: [
          { name: t("dashboard_text"), url: "/dashboard", icon: <DashboardIcon />, access: ["admin", "fleetadmin"], moduleKey: "dashboard" }
        ]
      },
      {
        label: "Bookings",
        items: [
          { name: t("booking_history"), url: "/bookings", icon: <ViewListIcon />, access: ["admin", "fleetadmin", "driver", "customer"], moduleKey: "bookingHistory" },
          { name: label("booking_orders_for_bid", "Booking Orders for Bid"), url: "/booking-orders-for-bid", icon: <ViewListIcon />, access: ["admin", "fleetadmin"], moduleKey: "bookingOrdersForBid" },
          { name: label("admin_bid_monitoring", "Bid Monitoring"), url: "/admin-bid-monitoring", icon: <AssessmentIcon />, access: ["admin"], moduleKey: "adminBidMonitoring" },
          { name: t("addbookinglable"), url: "/addbookings", icon: <ContactPhoneIcon />, access: calcEst && !showEst && !optionsRequired ? ["customer"] : ["admin", "fleetadmin", "customer"], moduleKey: "addBookings" }
        ]
      },
      {
        label: "Operations",
        items: [
          { name: label("empty_container_return", "Empty Container Return"), url: "/empty-container-returns", icon: <AssignmentReturnIcon />, access: ["admin", "fleetadmin", "driver", "customer"], moduleKey: "emptyContainerReturn" },
          { name: label("container_yards_title", "Container Yard"), url: "/containeryards", icon: <WarehouseIcon />, access: ["admin"], moduleKey: "containerYard" },
          { name: label("data_import_export", "Import / Export"), url: "/data-import-export", icon: <ImportExportIcon />, access: ["admin"], moduleKey: "dataImportExport" },
          { name: "ECall", url: "/sos", icon: <SosIcon />, access: ["admin"], moduleKey: "ec" },
          { name: t("complain"), url: "/complain", icon: <AnnouncementOutlinedIcon />, access: ["admin"], moduleKey: "complaint" }
        ]
      },
      {
        label: "Clients",
        items: [
          { name: t("user"), url: "/users/0", icon: <EmojiPeopleIcon />, access: ["admin", "fleetadmin"], moduleKey: "users" },
          { name: t("profile"), url: "/profile", icon: <AccountCircleIcon />, access: ["admin", "fleetadmin", "driver", "customer"], moduleKey: "profile", alwaysVisible: true }
        ]
      },
      {
        label: "Fleet Management",
        items: [
          { name: t("cars"), url: "/cars", icon: <DirectionsCarIcon />, access: ["admin", "fleetadmin", "driver"], moduleKey: "carsAndDrivers" },
          { name: t("car_type"), url: "/cartypes", icon: <DirectionsCarIcon />, access: ["admin"], moduleKey: "vehicleType" }
        ]
      },
      {
        label: "Finance",
        items: [
          { name: label("additional_fees_title", "Additional Fees"), url: "/additionalfees", icon: <ReceiptIcon />, access: ["admin"], moduleKey: "additionalFeesTitle" },
          { name: t("withdraws_web"), url: "/withdraws", icon: <MoneyIcon />, access: ["admin"], moduleKey: "withdraws" },
          { name: t("add_to_wallet"), url: "/addtowallet", icon: <AccountBalanceWalletIcon />, access: ["admin"], moduleKey: "addToWallet" },
          { name: t("my_wallet_tile"), url: "/userwallet", icon: <AccountBalanceWalletIcon />, access: ["driver", "customer", "fleetadmin"], moduleKey: "myWallet" },
          { name: t("payment_settings"), url: "/paymentsettings", icon: <PhoneIphoneIcon />, access: ["admin"], moduleKey: "paymentSettings" }
        ]
      },
      {
        label: "Communication",
        items: [
          { name: label("admin_chat_proof", "Chat Proof"), url: "/adminchats", icon: <ChatIcon />, access: ["admin"], moduleKey: "chatProof" },
          { name: t("push_notifications"), url: "/notifications", icon: <NotificationsActiveIcon />, access: ["admin"], moduleKey: "pushNotifications" },
          { name: t("promo"), url: "/promos", icon: <LocalOfferIcon />, access: ["admin"], moduleKey: "promos" }
        ]
      },
      {
        label: "Website",
        items: [
          { name: "Website Overview", url: "/website-management", icon: <PublicIcon />, access: websiteAdminAccess, moduleKey: "settings" },
          { name: "Landing Page", url: "/website-management/landing", icon: <ArticleIcon />, access: websiteAdminAccess, moduleKey: "settings" },
          { name: "About Us", url: "/website-management/about", icon: <InfoOutlinedIcon />, access: websiteAdminAccess, moduleKey: "settings" },
          { name: "Contact Us", url: "/website-management/contact", icon: <ContactSupportOutlinedIcon />, access: websiteAdminAccess, moduleKey: "settings" },
          { name: "Terms & Conditions", url: "/website-management/terms", icon: <GavelOutlinedIcon />, access: websiteAdminAccess, moduleKey: "settings" },
          { name: "Privacy Policy", url: "/website-management/privacy", icon: <PolicyOutlinedIcon />, access: websiteAdminAccess, moduleKey: "settings" },
          { name: "Branding", url: "/website-management/branding", icon: <PaletteOutlinedIcon />, access: websiteAdminAccess, moduleKey: "settings" },
          { name: "Website Settings", url: "/website-management/settings", icon: <SettingsOutlinedIcon />, access: websiteAdminAccess, moduleKey: "settings" }
        ]
      },
      {
        label: "System",
        items: [
          { name: t("report"), url: "/allreports", icon: <AssessmentIcon />, access: ["admin", "fleetadmin"], moduleKey: "reports" },
          { name: t("settings_title"), url: "/settings", icon: <PhoneIphoneIcon />, access: ["admin"], moduleKey: "settings" },
          { name: label("workflow_configuration", "Workflow Configuration"), url: "/workflow-configuration", icon: <AccountTreeIcon />, access: ["admin"], moduleKey: "workflowConfiguration" },
          { name: t("logout"), url: "logout", icon: <ExitToAppIcon />, access: ["admin", "fleetadmin", "driver", "customer"], moduleKey: "logout", alwaysVisible: true }
        ]
      }
    ];
  }, [t, label]);

  const visibleGroups = useMemo(() => {
    if (!role) return [];

    return navGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => canViewSidebarItem({ role, item, accessPermissions }))
      }))
      .filter((group) => group.items.length > 0);
  }, [accessPermissions, navGroups, role]);

  const pageTitle = location.pathname === "/" ? t("home") : getPageTitle(location.pathname, visibleGroups);
  const headerHomeItem = { name: t("home"), url: "/", icon: <HomeIcon />, access: ["admin", "fleetadmin", "driver", "customer"], moduleKey: "home" };
  const drawerWidth = collapsed && !isTabletDown ? collapsedWidth : expandedWidth;
  const container = window !== undefined ? () => window().document.body : undefined;
  const sidebarClassName = collapsed && !isTabletDown ? "garahex-sidebarPanel garahex-collapsed" : "garahex-sidebarPanel";

  const renderNavItem = (item) => {
    const active = isActivePath(location.pathname, item);
    const isLogout = item.url === "logout";
    const isDisabled = !item.url && !isLogout;
    const button = (
      <ListItemButton
        className="garahex-navButton"
        selected={active}
        disabled={isDisabled}
        component={!isDisabled && !isLogout ? Link : "button"}
        to={!isDisabled && !isLogout ? item.url : undefined}
        onClick={() => {
          if (isLogout) {
            handleSignOff();
            return;
          }
          if (!isDisabled) {
            setMobileOpen(false);
          }
        }}
        aria-label={item.name}
      >
        <ListItemIcon className="garahex-navIcon">{item.icon}</ListItemIcon>
        <ListItemText
          className="garahex-navText"
          primary={item.name}
          secondary={item.helper}
          primaryTypographyProps={{ fontSize: "0.78rem", fontWeight: active ? 850 : 700, fontFamily: FONT_FAMILY }}
          secondaryTypographyProps={{ fontSize: "0.62rem", color: "rgba(226, 241, 255, 0.5)", fontFamily: FONT_FAMILY }}
        />
      </ListItemButton>
    );

    return collapsed && !isTabletDown ? (
      <Tooltip key={item.moduleKey + item.name} title={item.helper ? `${item.name} — ${item.helper}` : item.name} placement={isRTL ? "left" : "right"}>
        <span>{button}</span>
      </Tooltip>
    ) : (
      <span key={item.moduleKey + item.name}>{button}</span>
    );
  };

  const drawer = (
    <Box className={sidebarClassName} sx={{ position: "relative", width: drawerWidth }}>
      <Box className="garahex-sidebarContent">
        <Box className="garahex-brandBlock">
          <Box className="garahex-brandLockup">
            <img src={garahexLogo} alt="Garahex" />
          </Box>
        </Box>

        <Box className="garahex-sidebarSearch" aria-label="Sidebar filter visual">
          <Box className="garahex-searchField" sx={{ minWidth: 0, height: adminTokens.density.controlHeight, background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.72)" }}>
            <SearchIcon fontSize="small" />
            {!collapsed || isTabletDown ? (
              <InputBase placeholder="Find admin page" inputProps={{ "aria-label": "Find admin page" }} sx={{ color: "inherit", fontSize: "0.78rem" }} />
            ) : null}
          </Box>
        </Box>

        <Box className="garahex-sidebarScroll">
          {role ? (
            visibleGroups.map((group) => (
              <Box className="garahex-navGroup" key={group.label}>
                <div className="garahex-navGroupLabel">{group.label}</div>
                <List disablePadding>{group.items.map(renderNavItem)}</List>
              </Box>
            ))
          ) : null}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box className="garahex-admin-shell" sx={{ display: "flex", direction: isRTL ? "rtl" : "ltr" }}>
      <CssBaseline />

      <AppBar
        className="garahex-adminHeader"
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: isRTL ? 0 : { md: `${drawerWidth}px` },
          mr: isRTL ? { md: `${drawerWidth}px` } : 0,
          transition: `width ${adminTokens.motion.drawer}, margin ${adminTokens.motion.drawer}`
        }}
      >
        <Toolbar className="garahex-headerToolbar">
          <IconButton
            className="garahex-headerIconButton"
            aria-label={isTabletDown ? "Open admin navigation" : collapsed ? "Expand admin navigation" : "Collapse admin navigation"}
            onClick={() => (isTabletDown ? setMobileOpen(true) : setCollapsed((value) => !value))}
          >
            {isTabletDown ? <MenuIcon /> : collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>

          <Box className="garahex-headerTitle">
            <div className="garahex-breadcrumb">{appName} / Admin</div>
            <h1 className="garahex-pageTitle">{pageTitle}</h1>
          </Box>

          <Box className="garahex-searchField" role="search">
            <SearchIcon fontSize="small" />
            <InputBase placeholder="Search operations" inputProps={{ "aria-label": "Search operations" }} sx={{ color: "inherit", fontSize: "0.8rem" }} />
          </Box>

          {canViewSidebarItem({ role, accessPermissions, item: headerHomeItem }) ? (
            <Tooltip title={headerHomeItem.name}>
              <IconButton className="garahex-headerIconButton" aria-label={headerHomeItem.name} onClick={() => navigate(headerHomeItem.url)}>
                <HomeIcon />
              </IconButton>
            </Tooltip>
          ) : null}

          {canViewSidebarItem({ role, accessPermissions, item: { access: ["admin"], moduleKey: "pushNotifications" } }) ? (
            <Tooltip title="Notifications">
              <IconButton className="garahex-headerIconButton" aria-label="Open notifications" onClick={() => navigate("/notifications")}>
                <NotificationsNoneIcon />
              </IconButton>
            </Tooltip>
          ) : null}

          {canViewSidebarItem({ role, accessPermissions, item: { access: ["admin"], moduleKey: "chatProof" } }) ? (
            <Tooltip title="Messages">
              <IconButton className="garahex-headerIconButton" aria-label="Open admin chats" onClick={() => navigate("/adminchats")}>
                <ForumOutlinedIcon />
              </IconButton>
            </Tooltip>
          ) : null}

          <button className="garahex-profileChip" type="button" onClick={(event) => setProfileAnchor(event.currentTarget)} aria-haspopup="menu" aria-expanded={Boolean(profileAnchor)}>
            <Avatar sx={{ width: 30, height: 30, fontSize: "0.78rem", fontWeight: 850, bgcolor: adminTokens.colors.royalBlue }}>
              {getInitials(profileName)}
            </Avatar>
            <span className="garahex-profileText">
              <Typography component="span" sx={{ display: "block", fontWeight: 850, fontSize: "0.78rem", lineHeight: 1.05 }}>
                {profileName}
              </Typography>
              <span className="garahex-rolePill">{humanizeRole(role)}</span>
            </span>
          </button>

          <Menu anchorEl={profileAnchor} open={Boolean(profileAnchor)} onClose={() => setProfileAnchor(null)}>
            <MenuItem
              onClick={() => {
                setProfileAnchor(null);
                navigate("/profile");
              }}
            >
              Profile
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setProfileAnchor(null);
                handleSignOff();
              }}
            >
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" aria-label="Admin navigation" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 }, transition: `width ${adminTokens.motion.drawer}` }}>
        <Drawer
          container={container}
          variant="temporary"
          anchor={isRTL ? "right" : "left"}
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: expandedWidth, border: 0 }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor={isRTL ? "right" : "left"}
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              border: 0,
              overflow: "hidden",
              transition: `width ${adminTokens.motion.drawer}`
            }
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        className="garahex-admin-main"
        sx={{ width: { md: `calc(100% - ${drawerWidth}px)` }, transition: `width ${adminTokens.motion.drawer}` }}
      >
        <Box className="garahex-admin-mainInner">
          <Box className="garahex-mainSurface">
            <Box className="garahex-mainContent">{children}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
